import { useState, useEffect } from 'react';
import { API_TOKEN_KEY } from '../constants/localStorageConstants';
import { getApiUrl } from './config';


/**
 * Executes a simple get against the provided URL. T is the expected
 * return type of the data.
 * @param url Path to query for data
 * @returns Promise
 */
export function useFetchTyped<T>(url: string) {
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function init() {
      try {
        // Set up standard request headers
        const req: Request = new Request(`${getApiUrl()}/${url}`, {
          headers: {
            'Cache-Control': 'no-cache',
            member_id: `${sessionStorage.getItem('memberId')}`,
          },
        });

        // Make the request
        const response = await fetch(req);

        // If good, pull out response data
        if (response.ok) {
          const json = await response.json();
          setData(json);
        } else {
          throw response;
        }
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    }

    init();
  }, [url]);

  return { data, error, loading };
}

/**
 * Executes a simple get against the provided URL.
 * @param url Path to query for data
 * @returns Promise
 */
export default function useFetch(url: string) {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function init() {
      try {
        // Set up standard request headers
        const req: Request = new Request(`${getApiUrl()}/${url}`, {
          headers: {
            'Cache-Control': 'no-cache',
            member_id: `${sessionStorage.getItem('member_id')}`,
          },
        });

        // Make the request
        const response = await fetch(req);

        // If good, pull out response data
        if (response.ok) {
          const json = await response.json();
          setData(json);
        } else {
          throw response;
        }
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    }

    init();
  }, [url]);

  return { data, error, loading };
}

/**
 * Return resource at BASE_URL/url
 * @param url URL fragment for resource to GET
 * @returns Promise
 */
export function getApi(url: string, headers: any = undefined) {
  return baseApiCall(`${getApiUrl()}/${url}`, 'GET', undefined, headers);
}

/**
 * Post a new resource at BASE_URL/url
 * @param url URL fragment for resource to POST
 * @param postData Data to post
 * @returns Promise
 */
export function postApi(url: string, postData: {}, headers: any = undefined) {
  return baseApiCall(`${getApiUrl()}/${url}`, 'POST', JSON.stringify(postData), headers);
}

/**
 * Put a new resource at BASE_URL/url
 * @param url URL fragment for resource to PUT
 * @param postData Data to put
 * @returns Promise
 */
export function putApi(url: string, putData: {}, headers: any = undefined) {
  return baseApiCall(`${getApiUrl()}/${url}`, 'PUT', JSON.stringify(putData), headers);
}

/**
 * Delete the resources at BASE_URL/url?id=id
 * @param url URL fragment for resource to DELETE
 * @param id ID of the resource to delete
 * @returns Promise
 */
export function deleteApi(url: string, id: number) {
  return baseApiCall(`${getApiUrl()}/${url}/${id}`, 'DELETE');
}

export function deleteDataApi(url: string, deleteData: {}, headers: any = undefined) {
  return baseApiCall(`${getApiUrl()}/${url}`, 'DELETE', JSON.stringify(deleteData), headers);
}

/**
 * Base method for HTTP calls to the API server
 * @param url URL to request
 * @param method HTTP method to use
 * @param body Any data passed in with the request. Defaults to undefined.
 * @returns Promise
 */
function baseApiCall(url: string, method: string, body: any = undefined, headers: any = undefined) {
  // Set up standard request headers
  if (headers === undefined) {
    headers = generateHeaders();
  }
  const req: Request = new Request(url, {
    method: method,
    headers: headers,
    body: body,
  });

  // Make the request
  return fetch(req);
}

function generateHeaders(): Record<string, string> {
  const token = localStorage.getItem(API_TOKEN_KEY);
  const memberId = sessionStorage.getItem('member_id');
  const isLocalEnv = window.location.hostname === 'localhost';

  const headers: any = {
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
    member_id: `${memberId}`,
  };
  
  if (isLocalEnv) {
    headers['authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export function useFetchUpdateUserReport(url: string, postData: {}) {
  // Set up standard request headers
  const req: Request = new Request(`${getApiUrl()}/${url}`, {
    method: 'PUT',
    headers: generateHeaders(),
    body: JSON.stringify(postData),
  });

  // Make the request
  return fetch(req);
}
